import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { ArrowRight, Translate } from "@phosphor-icons/react";

const HERO =
  "https://images.unsplash.com/photo-1665815844395-06f64f44b5e3?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Login() {
  const { user, login, error } = useAuth();
  const { t, lang, setLang, langs } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  if (user && typeof user === "object") return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    if (ok) nav("/", { replace: true });
  };

  return (
    <div
      className="min-h-screen grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] nylsu-noise"
      data-testid="login-page"
    >
      <div className="flex items-center justify-center px-6 py-16 md:px-16 relative">
        {/* Language switcher */}
        <div className="absolute top-6 right-6 flex items-center gap-1" data-testid="login-lang-switcher">
          <Translate size={14} weight="light" className="text-[#8F8D84]" />
          {langs.map((l) => (
            <button
              key={l.code}
              onClick={() => setLang(l.code)}
              data-testid={`login-lang-${l.code}`}
              className="px-2 py-1 text-[10px] uppercase tracking-[0.2em]"
              style={{
                color: lang === l.code ? "#1A1A1A" : "#8F8D84",
                borderBottom: lang === l.code ? "1px solid var(--nylsu-gold)" : "1px solid transparent",
              }}
            >
              {l.short}
            </button>
          ))}
        </div>

        <div className="w-full max-w-md">
          <div className="mb-14">
            <div className="font-serif text-3xl tracking-[0.35em] leading-none">{t("common.appName")}</div>
            <div className="overline mt-3">{t("common.appTagline")}</div>
          </div>

          <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-none mb-4">
            {t("auth.signIn")}
          </h1>
          <p className="text-sm text-[#5C5A54] mb-10">{t("auth.subtitle")}</p>

          <form onSubmit={submit} className="space-y-8" data-testid="login-form">
            <div>
              <label className="data-label block mb-2">{t("common.email")}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                autoComplete="email"
                data-testid="login-email-input"
                className="w-full bg-transparent border-0 border-b py-2 outline-none focus:border-[#C2A878] transition-colors text-base"
                style={{ borderColor: "var(--nylsu-border-strong)" }}
              />
            </div>
            <div>
              <label className="data-label block mb-2">{t("common.password")}</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="login-password-input"
                className="w-full bg-transparent border-0 border-b py-2 outline-none focus:border-[#C2A878] transition-colors text-base"
                style={{ borderColor: "var(--nylsu-border-strong)" }}
              />
            </div>

            {error && (
              <div
                className="text-sm py-3 px-4 border"
                style={{ borderColor: "#E8CFCF", background: "#FBF3F3", color: "#8C3B3B" }}
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full inline-flex items-center justify-between px-6 py-4 text-sm uppercase tracking-[0.2em] transition-colors disabled:opacity-60"
              style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}
            >
              <span>{submitting ? t("auth.signingIn") : t("auth.enter")}</span>
              <ArrowRight size={16} weight="light" />
            </button>
          </form>

          <div className="mt-14 text-xs text-[#8F8D84] leading-relaxed">
            {t("auth.restricted")} NYLSU © {new Date().getFullYear()}.
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img
          src={HERO}
          alt="NYLSU showroom"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(26,26,26,0.15), rgba(26,26,26,0.55))" }}
        />
        <div className="absolute bottom-14 left-14 right-14 text-white/95">
          <div className="overline text-white/70 mb-5">{t("auth.heroEyebrow")}</div>
          <div className="font-serif font-light text-4xl leading-tight tracking-tight">
            {t("auth.heroTagline")}
          </div>
        </div>
      </div>
    </div>
  );
}
